/**
 * PricingSection Component
 * Pricing tiers overview
 */

import { TIER_FEATURES } from '@parlay-picker/shared/subscriptionTier';
import { Check } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  cta: string;
  ctaLink: string;
}

const TIERS: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Perfect for getting started',
    features: TIER_FEATURES.free,
    cta: 'Get Started Free',
    ctaLink: '/register',
  },
  {
    name: 'Pro',
    price: '$24.99',
    period: 'per month',
    description: 'For serious bettors',
    features: TIER_FEATURES.pro,
    cta: 'Start Pro Trial',
    ctaLink: '/register',
  },
  {
    name: 'Premium',
    price: '$39.99',
    period: 'per month',
    description: 'Maximum edge, maximum features',
    features: TIER_FEATURES.premium,
    cta: 'Go Premium',
    ctaLink: '/register',
  },
  {
    name: 'Elite',
    price: '$79.99',
    period: 'per month',
    description: 'Sharp money edge for serious bettors',
    features: TIER_FEATURES.elite,
    highlighted: true,
    cta: 'Go Elite',
    ctaLink: '/register',
  },
];

export function PricingSection(): ReactElement {
  return (
    <section id="pricing" className="py-20 bg-theme-bg">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-theme-text mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-theme-text-secondary max-w-2xl mx-auto">
            Start free and upgrade when you&apos;re ready. All plans include core features, no
            hidden fees.
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {TIERS.map((tier, index) => (
            <div
              key={index}
              className={`relative p-8 rounded-xl border-2 ${
                tier.highlighted
                  ? 'border-theme-primary bg-gradient-to-b from-theme-primary/5 to-theme-bg'
                  : 'border-theme-border bg-theme-surface'
              } transition hover:scale-105`}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-theme-primary-fill text-theme-on-primary-fill text-sm font-semibold rounded-full">
                  Most Popular
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-theme-text mb-2">{tier.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-theme-text">{tier.price}</span>
                  <span className="text-theme-text-tertiary ml-2">{tier.period}</span>
                </div>
                <p className="text-sm text-theme-text-secondary">{tier.description}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-theme-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-theme-text-secondary">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={tier.ctaLink}
                className={`block w-full py-3 rounded-lg font-semibold text-center transition ${
                  tier.highlighted
                    ? 'bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill shadow-lg shadow-theme-primary/25'
                    : 'bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text border border-theme-border'
                }`}
              >
                {tier.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <p className="text-center text-sm text-theme-text-tertiary mt-12">
          All plans include 14-day money-back guarantee. Cancel anytime.
        </p>
      </div>
    </section>
  );
}
